import { useCallback, useEffect, useRef } from "react";

export interface IShortcut {
  shortcut: string;
  callback: (event: KeyboardEvent) => void;
}

// create a consistent mapping for key names EG: ctrl, control and Control should all map to control
const remappedKeyNames: Record<string, string> = { ctrl: "control" };

/**
 * A custom hook to listen for keyboard shortcuts.
 * @param shortcuts An array of shortcut configurations.
 */
export const useShortcuts = (shortcuts: IShortcut[]) => {
  // Store the key combinations. EG control+delete would be stored as ['control', 'delete']
  const downKeysRef = useRef<Set<string>>(new Set());

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      const key = remappedKeyNames[event.key?.toLowerCase()] ?? event.key?.toLowerCase();
      downKeysRef.current.add(key);

      const sourceElement = event.target as HTMLElement;

      // if the control is an input, textarea or content editable, ignore the shortcut
      if (sourceElement.tagName === "INPUT" ||
        sourceElement.tagName === "TEXTAREA") {
        return;
      }

      shortcuts.forEach(({ shortcut, callback }) => {
        const shortcutKeys = shortcut
          .toLowerCase()
          .split("+")
          .map((k) => remappedKeyNames[k] ?? k);

        const allShortcutKeysPressed = shortcutKeys.every((k) => downKeysRef.current.has(k));

        if (allShortcutKeysPressed && shortcutKeys.length === downKeysRef.current.size) {
          event.preventDefault();
          callback(event);
        }
      });
    },
    [shortcuts]
  );

  const handleKeyUp = useCallback((event: KeyboardEvent) => {
    const key = remappedKeyNames[event.key?.toLowerCase()] ?? event.key?.toLowerCase();
    downKeysRef.current.delete(key);
  }, []);

  useEffect(() => {
    // Clear keys if the user tabs away
    const handleBlur = () => {
      downKeysRef.current.clear();
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keyup", handleKeyUp);
    window.addEventListener("blur", handleBlur);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", handleBlur);
      downKeysRef.current.clear();
    };
  }, [handleKeyDown, handleKeyUp]);
};
